'use client';
import {Variant} from "@/types";
import {SearchResponse} from "meilisearch";
import {useEffect, useState, useMemo, useRef} from "react";
import {getVariantsAction} from "@/app/actions/variant-actions";
import {Button} from "@/shadcn/components/ui/button";
import VariantGrid from "@/components/variants/variant-grid";
import {useFilterStore, useVariantStore} from "@/store/variants-store";
import {usePathname, useSearchParams} from "next/navigation";
import {VariantSkeletonGrid} from "./variant-skeleton-grid";
import {AsyncErrorBoundary} from "@/components/error-boundary";
import {declusterByBrand} from "@/lib/decluster";

// Altlast: Seiten übergeben buildFilter-Objekte, der Store erwartet SearchParams;
// die Typen passen nicht exakt zusammen (sort enthält undefined). Bewusst offen gelassen.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VariantFilter = any;

const VariantInfiniteScroll = ({
                                   initialVariants,
                                   initialFilter = null,
                                   initialPage = 1
                               }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialVariants: SearchResponse<Variant, any>;
    initialFilter: VariantFilter | null;
    initialPage: number;
}) => {
    const [end, setEnd] = useState(false);
    // For server-side pagination.
    const [isLoading, setIsLoading] = useState(false);
    const [noProductsFound, setNoProductsFound] = useState(false);
    const [stop, setStop] = useState(false);
    const BATCH_SIZE = 144;

    // Synchronous lock against the scroll-driven effect race: the auto-fetch
    // effect can re-enter before the async setIsLoading(true) is observed.
    // This ref blocks re-entry within the same microtask.
    const isFetchingRef = useRef(false);

    // Position in der Trefferliste des Servers (nicht Zahl der angezeigten
    // Produkte): Seite 1 der Suche ist neu sortiert und Doppelte werden beim
    // Nachladen übersprungen, angezeigte und geladene Anzahl können abweichen.
    // null = unbekannt (z. B. nach Wiederherstellung aus dem Store) → Anzeigezahl.
    const fetchedOffsetRef = useRef<number | null>(null);

    // These both are needed to prevent the grid from jumping up and down when new data is fetched.
    // We check if variants changed and if so, we set the stop state to true.
    // We check if the filters.filter and filter.sort changed and if so, we set the stop state to true.
    const {variants, pageKey: storePageKey, setVariants, appendVariants} = useVariantStore();
    const {filter, pageKey: filterPageKey, setFilter} = useFilterStore();

    const [page, setPage] = useState<number>(initialPage);
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const [renderFinished, setRenderFinished] = useState(false);

    // Bei explizitem User-Sort (Preis, Rabatt, Rating) muss die Reihenfolge streng
    // monoton bleiben — entzerrt wird nur die Default-Reihenfolge (Quality-Score).
    const userSort = searchParams?.get('sort') || '';

    // Spread same-brand tiles in the initial (server-rendered) batch so near-identical
    // variants of one series don't pile up at the top. Deterministic + computed the same
    // way on server and client, so there is no hydration mismatch.
    const initialHits = useMemo(
        () => (userSort ? initialVariants.hits : declusterByBrand(initialVariants.hits)),
        [initialVariants, userSort]
    );

    const currentPageKey = useMemo(() => {
        const sort = searchParams?.get('sort') || '';
        return `${pathname}?sort=${sort}`;
    }, [pathname, searchParams]);

    function createFilterHash(filter: VariantFilter | null): string {
        if (!filter) return '';

        // Only include the properties we care about
        const relevantProps = {
            filter: filter.filter,
            sort: filter.sort,
            q: filter.q
        };

        // Create a stable string representation
        return JSON.stringify(relevantProps);
    }

    useEffect(() => {
        setStop((fetchedOffsetRef.current ?? variants.length) % BATCH_SIZE === 0);
    }, [variants]);


    useEffect(() => {
        const initialHash = createFilterHash(initialFilter);
        const currentHash = createFilterHash(filter);

        // Only update filter if it changed AND we're on a matching page
        if (!filter || initialHash !== currentHash || filterPageKey !== currentPageKey) {
            setFilter(initialFilter, currentPageKey);
        }
    }, [initialFilter, currentPageKey]);


    useEffect(() => {
        const initialHash = createFilterHash(initialFilter);
        const currentHash = createFilterHash(filter);

        // Only update variants if:
        // 1. Store is empty and we have initial data, OR
        // 2. Filter changed, OR
        // 3. PageKey doesn't match (user navigated to different page/filter)
        if ((variants.length === 0 && initialHits) ||
            initialHash !== currentHash ||
            storePageKey !== currentPageKey) {
            fetchedOffsetRef.current = null;
            setVariants(initialHits, currentPageKey);
        }
    }, [initialVariants, currentPageKey]);

    useEffect(() => {
        if (initialHits.length === 0) {
            setNoProductsFound(true);
        } else {
            setNoProductsFound(false);
            // Check if we've loaded all available products
            const totalHits = initialVariants.estimatedTotalHits || 0;
            const loadedHits = initialHits.length;

            // If we've loaded all available products or we have fewer than 48 variants initially
            if (loadedHits >= totalHits || loadedHits < 48) {
                setEnd(true);
            } else {
                setEnd(false);
            }
        }
    }, [initialVariants]);

    const estimatedTotalHits = initialVariants.estimatedTotalHits || 0;

    // Use variants from store ONLY if pageKey matches (prevents hydration mismatch)
    // On server: storePageKey is null, so we use initialVariants
    // On client after hydration: if user navigated to same page, use store; otherwise use server data
    const shouldUseStoreVariants = storePageKey === currentPageKey && variants.length > 0;
    // Memoized so VariantGrid props stay reference-stable across renders that
    // don't actually swap the underlying source array. Without useMemo, every
    // re-render here forces VariantGrid (and the virtualizer) to recompute.
    const displayVariants = useMemo(
        () => (shouldUseStoreVariants ? variants : initialHits),
        [shouldUseStoreVariants, variants, initialHits]
    );
    const hasProducts = displayVariants.length > 0;

    // Function to fetch more data from the server.
    // manual = Klick auf „Mehr anzeigen“. Automatisches Nachladen (Scrollen)
    // hält an jeder 144er-Grenze an. Die Prüfung muss hier synchron passieren:
    // der `stop`-State wird erst nach dem nächsten Render gesetzt, bis dahin hatte
    // das Raster schon die nächste Seite angefordert (auf dem Handy 16 Seiten =
    // 816 Produkte bei 5× Scrollen, der Knopf erschien nie).
    const fetchMoreData = async (manual = false) => {
        if (isFetchingRef.current) return;
        const startOffset = fetchedOffsetRef.current ?? (shouldUseStoreVariants ? variants.length : initialHits.length);
        if (!manual && startOffset > 0 && startOffset % BATCH_SIZE === 0) {
            setStop(true);
            return;
        }
        isFetchingRef.current = true;
        try {
            setIsLoading(true);

            let offset = startOffset;

            // Seite 1 der Suche ist vom Reranker aus einem größeren Pool neu
            // sortiert (product-search.server.ts); Produkte daraus können per
            // Offset noch einmal kommen und werden hier übersprungen. Ein Block
            // nur aus Bekanntem wird direkt übersprungen (höchstens 3 Versuche).
            const shown = new Set((shouldUseStoreVariants ? variants : initialHits).map((v) => String(v.variant_id ?? v.ean)));
            let fetchedCount = 0;
            let freshHits: Variant[] = [];
            for (let attempt = 0; attempt < 3; attempt++) {
                const batch = await getVariantsAction({
                    ...initialFilter,
                    offset,
                });
                fetchedCount = batch.hits.length;
                offset += fetchedCount;
                freshHits = batch.hits.filter((v) => !shown.has(String(v.variant_id ?? v.ean)));
                if (fetchedCount === 0 || freshHits.length > 0) break;
            }
            fetchedOffsetRef.current = offset;

            if (fetchedCount === 0) {
                setEnd(true);
            }

            // Spread same-brand tiles within this freshly fetched batch (display-only,
            // deterministic). Per-batch so offset pagination and already-rendered tiles
            // stay stable. Skipped under explicit user sorts (strict monotonic order).
            const newHits = userSort ? freshHits : declusterByBrand(freshHits);

            // If this is the first load after hydration, set variants with new data combined
            if (!shouldUseStoreVariants) {
                setVariants([...initialHits, ...newHits], currentPageKey);
            } else {
                appendVariants(newHits);
            }
            setPage(parseInt(page.toString()) + 1);
        } catch (error) {
            console.error('Error fetching more variants:', error);
            setEnd(true); // Stop trying to fetch more data on error
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    };

    return (
        <>
            <AsyncErrorBoundary>
                {/* key={pathname} verwirft den useWindowVirtualizer-State bei
                    Kategorie-Wechsel — sonst restaurierte tanstack/virtual den
                    Scroll der alten Page nach unserem Scroll-Reset. */}
                <VariantGrid
                    key={pathname}
                    variants={displayVariants}
                    fetchMoreData={fetchMoreData}
                    isLoading={isLoading}
                    estimatedTotalHits={estimatedTotalHits}
                    setRenderFinished={setRenderFinished}
                    end={end}
                    stop={stop}
                />
            </AsyncErrorBoundary>
            {(!hasProducts || isLoading) && !noProductsFound && <VariantSkeletonGrid count={48}/>}
            {/* Manueller "Mehr anzeigen"-Fallback — wie der "Alle geladen"-Block
                unten hart in der Höhe gedeckelt (überdeckte Skeletons erzeugten
                vorher ~2.500px tote Fläche vor dem Curated Content). */}
            {displayVariants.length !== 0 && !isLoading && renderFinished && !end && stop &&
                <div className="flex flex-col items-center justify-start text-center py-4 w-full relative max-h-72 overflow-hidden transition-opacity duration-400 ease-in-out starting:opacity-0 opacity-100">
                    <VariantSkeletonGrid count={12} disableAnimation={true}/>
                    <div className="absolute top-0 w-full h-full bg-linear-to-b from-transparent to-gray-100">
                        <div className="flex flex-col items-center justify-center h-full">
                            <h4 className="text-2xl font-extrabold">Weitere Produkte laden</h4>
                            <p className="mt-2 text-slate-600">
                                {displayVariants.length} von über {estimatedTotalHits} Produkten geladen
                            </p>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => fetchMoreData(true)}
                                className="mt-4"
                            >
                                Mehr anzeigen
                            </Button>
                        </div>
                    </div>
                </div>}
            {end && displayVariants.length > 0 && <div className="grow  flex-col items-center justify-start max-h-36 text-center py-4 w-full relative transition-opacity duration-400 ease-in-out starting:opacity-0 opacity-100 overflow-hidden">
                <VariantSkeletonGrid count={24} disableAnimation={true}/>
                <div className="absolute top-0 w-full h-36 bg-linear-to-b from-transparent to-gray-100 pt-16">
                    <div className="sticky top-12">
                        <h4 className="text-lg sm:text-xl md:text-2xl font-extrabold mt-6 text-slate-900 ">Alle {displayVariants.length} Produkte geladen</h4>
                    </div>
                </div>
            </div>}

        </>
    );
};

export default VariantInfiniteScroll;
